import { Component, Input, OnInit } from '@angular/core';
import { AiTopicSettingsService, Topic, SubTopic } from '../ai-topic-settings.service';
import { UserManagementService } from '../../user-management/user-management.service';
import { MatDialog } from '@angular/material/dialog';
import { TopicModalComponent } from '../topic-modal/topic-modal.component';
import { SubTopicModalComponent } from '../sub-topic-modal/sub-topic-modal.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { MessageService } from 'primeng/api';

@Component({
    selector: 'app-ai-topic',
    templateUrl: './ai-topic.component.html',
    styleUrls: ['./ai-topic.component.css'],
    standalone: false
})
export class AiTopicComponent implements OnInit {

  @Input() fromStepper: boolean = false;
  
  topics: Topic[] = [];
  selectedTopic: Topic | null = null;
  selectedSubTopics: SubTopic[] = [];
  loading = false;
  currentUser: any;
  currentSite: any;
  userId: number = 0;
  siteId: number = 0;

  constructor(
    private aiTopicService: AiTopicSettingsService,
    private userService: UserManagementService,
    private dialog: MatDialog,
    private messageService: MessageService
  ) { }

  ngOnInit(): void {
    this.initializeUserData();
    this.loadTopics();
  }

  private initializeUserData(): void {
    this.currentUser = this.userService.getUserDetail();
    this.currentSite = this.userService.getSiteDetail();
    
    if (this.currentUser) {
      this.userId = this.currentUser.id ? +this.currentUser.id : 0;
    }
    
    if (this.currentSite && this.currentSite.length > 0) {
      this.siteId = this.currentSite[0].site_id || 0;
    }
  }

  private loadTopics(): void {
    this.loading = true;
    this.aiTopicService.getTopicsWithSubtopics(this.userId, this.siteId).subscribe({
      next: (response) => {
        if (response.success) {
          this.topics = response.data;
          if (this.topics.length > 0) {
            this.selectedTopic = this.topics[0];
            this.selectedSubTopics = this.selectedTopic.sub_topics || [];
          } else {

            this.copySystemTopics();
          }
        } else {
          this.showError('Failed to load topics: ' + response.message);
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading topics:', error);
        this.showError('Error loading topics. Please try again.');
        this.loading = false;
      }
    });
  }

  private copySystemTopics(): void {
    if (this.userId === 1) {
      return;
    }
    this.loading = true;
    this.aiTopicService.copySystemTopics(this.siteId).subscribe({
      next: (response) => {
        if (response.success) {
          this.showSuccess('System topics copied successfully');
          this.loadTopics();
        } else {
          this.showError('Failed to copy system topics: ' + response.message);
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error copying system topics:', error);
        this.showError('Error copying system topics. Please try again.');
        this.loading = false;
      }
    });
  }

  onTopicChange(topic: Topic): void {
    this.selectedTopic = topic;
    this.selectedSubTopics = topic.sub_topics || [];
  }

  openAddTopicModal(): void {
    const dialogRef = this.dialog.open(TopicModalComponent, {
      width: '800px',
      data: { 
        siteId: this.siteId,
        userId: this.userId
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadTopics(); // Reload topics after adding
      }
    });
  }

  openAddSubTopicModal(): void {
    const dialogRef = this.dialog.open(SubTopicModalComponent, {
      width: '600px',
      data: { 
        mode: 'add',
        siteId: this.siteId,
        userId: this.userId
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadTopics(); // Reload topics after adding subtopic
      }
    });
  }

  editSubTopic(subTopic: SubTopic): void {
    const dialogRef = this.dialog.open(SubTopicModalComponent, {
      width: '600px',
      data: { 
        mode: 'edit',
        subTopic: subTopic,
        siteId: this.siteId,
        userId: this.userId
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadTopics(); // Reload topics after editing subtopic
      }
    });
  }

  deleteSubTopic(subTopic: SubTopic): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: `Are you sure you want to delete the subtopic "${subTopic.sub_topic_name}"?`,
        confirmText: 'Delete',
        cancelText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.aiTopicService.deleteSubTopic(subTopic.sub_topic_id).subscribe({
          next: (response) => {
            this.showSuccess('SubTopic deleted successfully');
            this.loadTopics();
          },
          error: (error) => {
            console.error('Error deleting subtopic:', error);
            this.showError('Error deleting subtopic. Please try again.');
          }
        });
      }
    });
  }


  private showSuccess(message: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: message
    });
  }

  private showError(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message
    });
  }
}
